/**
 * DeckChat — the one way to work with Lykke on a deck (2026-09-30).
 *
 * Like Claude Code: you say what you want, she does it on the deck, you look and say what to change. A new deck
 * from one sentence, a change to one page or all of them, a new page, a reorder, a question, "present it" — all
 * here. The editor stays for hands-on edits (click to type, drag); everything else is a message.
 */
import { useEffect, useRef, useState } from 'react';
import { ArrowUp, AudioLines, Check, Copy, Mic, MicOff, Phone, PhoneOff, Plus, RotateCcw, Sparkle, Square, Volume2, VolumeX } from 'lucide-react';
import { coworkApi, type DeckChatEvent } from '@/cowork/omniad/api/coworkApi';
import { RealtimeCall, type RealtimeTool } from '@/cowork/proposals/realtimeCall';

export interface ChatTurn { role: 'user' | 'assistant'; text: string; at?: string; /** Said on a call. */ voice?: boolean; decisions?: Array<{ question: string; options: string[] }>; /** Came from the saved history (not typed in this view). */ saved?: boolean }
/** The open choices: those of Lykke's last reply, when nothing was said after it. */
const openDecisions = (turns: ChatTurn[]) => { const last = turns[turns.length - 1]; return last?.role === 'assistant' ? last.decisions || [] : []; };

const START_IDEAS = [
  'A plan for our next campaign, based on what worked before',
  'Results of our latest campaign, for the client',
  'What we learned this year, and what to do next year',
];
const EDIT_IDEAS = ['Make it shorter and punchier', 'More visual — fewer words, bigger numbers', 'Translate to Swedish', 'Present it to me'];
const FOLLOW_UPS = ['Fewer words', 'More visual'];
const KIND_WORD: Record<string, string> = { results: 'Wrap-up', proposal: 'Proposal', creators: 'Creator deck', monthly: 'Monthly deck', showcase: 'Showcase', case: 'Case study' };

// The browser's own dictation (Chrome, Edge, Safari): the mic fills the box; nothing leaves the device but the text.
type Recognition = { lang: string; interimResults: boolean; continuous: boolean; onresult: (e: any) => void; onend: () => void; onerror: () => void; start: () => void; stop: () => void };
const SpeechRecognitionCtor: (new () => Recognition) | undefined = typeof window !== 'undefined' ? ((window as any).SpeechRecognition || (window as any).webkitSpeechRecognition) : undefined;

/** Lykke's words with the little formatting she uses: **bold** and "- " bullets. Text only — no HTML is injected. */
export function Said({ text }: { text: string }) {
  const inline = (line: string) => line.split(/(\*\*[^*]+\*\*)/g).map((part, i) => (part.startsWith('**') && part.endsWith('**') ? <strong key={i} className="font-semibold text-[#1c1917]">{part.slice(2, -2)}</strong> : <span key={i}>{part}</span>));
  const blocks: Array<{ bullet: boolean; lines: string[] }> = [];
  for (const raw of text.split('\n')) {
    const line = raw.trimEnd(); if (!line.trim()) { blocks.push({ bullet: false, lines: [] }); continue; }
    const bullet = /^\s*[-•*]\s+/.test(line);
    const last = blocks[blocks.length - 1];
    if (last && last.bullet === bullet && last.lines.length) last.lines.push(line.replace(/^\s*[-•*]\s+/, '')); else blocks.push({ bullet, lines: [line.replace(/^\s*[-•*]\s+/, '')] });
  }
  return (
    <div className="space-y-2.5">
      {blocks.filter((b) => b.lines.length).map((b, i) => (b.bullet
        ? <ul key={i} className="list-disc space-y-1 pl-5 marker:text-[#a8a29e]">{b.lines.map((l, j) => <li key={j}>{inline(l)}</li>)}</ul>
        : <p key={i}>{b.lines.map((l, j) => <span key={j}>{inline(l)}{j < b.lines.length - 1 ? <br /> : null}</span>)}</p>))}
    </div>
  );
}

/** What went wrong, in words a person can act on (the raw message stays in the console). */
function friendlyError(raw: string): string {
  console.warn('[deck-chat]', raw);
  if (/still working on this deck/i.test(raw)) return 'Lykke is still on another message for this deck — try again when she is done.';
  if (/overloaded|529|rate.?limit|429|too many requests/i.test(raw)) return 'Claude is very busy right now. Try again in a minute — nothing was lost.';
  if (/failed to fetch|network|load failed|ECONNRESET|socket/i.test(raw)) return 'The connection dropped. Pages that were already written are saved — send it again to finish.';
  if (/another workspace|not shared/i.test(raw)) return raw;
  // Internals (parser positions, stack text) mean nothing to a colleague: they stay in the console above.
  return 'Lykke stopped before she finished. The pages already written are saved — send it again to finish.';
}

export function DeckChat({ presentationId, index, pageCount, brandName, brandId, initialAsk, onInitialAskUsed, externalAsk, onBeforeRun, onRunStart, onEvent, onRunEnd, readiness, onReview, onShare }: {
  presentationId: string;
  index: number;
  pageCount: number;
  brandName?: string;
  brandId?: string;
  /** A message to send as soon as the chat opens (a deck started from Deck studio's box). */
  initialAsk?: string | null;
  onInitialAskUsed?: () => void;
  /** An instruction from Lykke the guide (top bar): sent as if typed here, once per `n`. */
  externalAsk?: { text: string; n: number } | null;
  /** Save pending edits before Lykke reads the deck. */
  onBeforeRun: () => Promise<void>;
  onRunStart: () => void;
  onEvent: (ev: DeckChatEvent) => void;
  onRunEnd: (changed: boolean) => void;
  /** The fact check after a build: running, how many figures still need a look, or clean. */
  readiness?: { running: boolean; open: number; clean: boolean; verified?: number; figures?: number };
  onReview?: () => void;
  onShare?: () => void;
}) {
  const [turns, setTurns] = useState<ChatTurn[]>([]);
  // Choices only the person can make ("which budget?"), one tap each — shown under Lykke's latest reply.
  const [decisions, setDecisions] = useState<Array<{ question: string; options: string[] }>>([]);
  // A deck built from nothing ends with a 'ready' card: what it is, how many pages, and the next step one tap away.
  const [ready, setReady] = useState<{ kind?: string; pages: number } | null>(null);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const [listening, setListening] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const recRef = useRef<Recognition | null>(null);
  const askedRef = useRef(false);
  // A message of ours is on its way: 'working' on the server is us, not someone else.
  const inFlightRef = useRef(false);
  const aliveRef = useRef(true);
  const waitingRef = useRef(false);

  useEffect(() => {
    let cancelled = false; aliveRef.current = true;
    // History goes above anything already said in this session (a first message can be on its way already).
    coworkApi.presentations.chatHistory(presentationId).then((r) => {
      if (cancelled) return;
      // Saved turns replace saved turns (a remount never doubles them); what was said in this view since stays after.
      setTurns((cur) => [...(r.turns || []).map((t) => ({ ...t, saved: true })), ...cur.filter((t) => !t.saved)]);
      if (!inFlightRef.current) setDecisions(openDecisions(r.turns || []));
      // Lykke is mid-message on this deck (another tab, another person): wait with her, then catch up.
      if (r.working && !inFlightRef.current) void waitForOther();
    }).catch(() => undefined);
    // The editor hit 'Lykke is working' on save: wait with her right away.
    const onBusy = (e: Event) => { if ((e as CustomEvent<string>).detail === presentationId && !inFlightRef.current) void waitForOther(); };
    window.addEventListener('deck-chat-busy', onBusy);
    // While idle, notice a message someone else started on this deck (another tab, a colleague) and wait with it.
    const idle = setInterval(() => {
      if (inFlightRef.current || waitingRef.current || document.hidden) return;
      void coworkApi.presentations.chatHistory(presentationId).then((r) => { if (r.working && !inFlightRef.current && !waitingRef.current && aliveRef.current) void waitForOther(); }).catch(() => undefined);
    }, 15000);
    // Leaving does not stop Lykke: she finishes and the deck is saved on the server either way.
    return () => { cancelled = true; aliveRef.current = false; clearInterval(idle); window.removeEventListener('deck-chat-busy', onBusy); recRef.current?.stop(); };
  }, [presentationId]);
  // The editor's review flow asks Lykke to fix a figure through here.
  useEffect(() => {
    const onAsk = (e: Event) => { const d = (e as CustomEvent<{ id: string; text: string }>).detail; if (d?.id === presentationId) void sendRef.current?.(d.text); };
    window.addEventListener('deck-chat-send', onAsk); return () => window.removeEventListener('deck-chat-send', onAsk);
  }, [presentationId]);
  useEffect(() => { const el = scrollRef.current; if (el && typeof el.scrollTo === 'function') el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' }); }, [turns, status]);

  const waitForOther = async () => {
    if (waitingRef.current || inFlightRef.current) return;
    waitingRef.current = true;
    setBusy(true); setStatus('Lykke is working on this deck (started elsewhere)'); onRunStart();
    try {
      for (let i = 0; i < 200 && aliveRef.current; i++) {
        await new Promise((r) => setTimeout(r, 3000));
        if (!aliveRef.current) return;
        const r = await coworkApi.presentations.chatHistory(presentationId).catch(() => null);
        if (r && !r.working) { setTurns((r.turns || []).map((t) => ({ ...t, saved: true }))); setDecisions(openDecisions(r.turns || [])); break; }
      }
    } finally {
      waitingRef.current = false;
      if (aliveRef.current) { setBusy(false); setStatus(''); onRunEnd(true); }
    }
  };

  const sendRef = useRef<((m: string, o?: { voice?: boolean }) => Promise<string>) | null>(null);
  const send = async (message: string, opts: { voice?: boolean } = {}): Promise<string> => {
    const text = message.trim(); if (!text || inFlightRef.current || (busy && !opts.voice)) return '';
    let said = '';
    inFlightRef.current = true;
    setDraft(''); setBusy(true); setStatus('Reading the deck'); setDecisions([]); setReady(null);
    const fresh = pageCount <= 1;
    let built: { kind?: string; pages: number } | null = null;
    setTurns((t) => [...t, { role: 'user', text, voice: opts.voice }, { role: 'assistant', text: '' }]);
    const ctrl = new AbortController(); abortRef.current = ctrl;
    let changed = false;
    try {
      await onBeforeRun();
      onRunStart();
      await coworkApi.presentations.chat(presentationId, { message: text, index }, (ev) => {
        if (ev.event === 'status') setStatus(ev.label);
        else if (ev.event === 'text') { said += ev.delta; setTurns((t) => { const n = t.slice(); const last = n[n.length - 1]; n[n.length - 1] = { ...last, text: last.text + ev.delta }; return n; }); }
        else if (ev.event === 'page' && ev.state === 'writing') setStatus((s) => (s.startsWith('Writing') || s.startsWith('Updating') ? s : 'Composing the pages'));
        else if (ev.event === 'decisions') setDecisions(ev.items || []);
        else if (ev.event === 'done') changed = ev.changed;
        if (ev.event === 'deck') { changed = true; built = { kind: (ev as { kind?: string }).kind, pages: ev.slides.filter((sl) => !(sl as { _pending?: unknown })._pending).length }; }
        onEvent(ev);
      }, ctrl.signal);
    } catch (e) {
      const msg = (e as Error).name === 'AbortError' ? 'Stopped.' : friendlyError((e as Error).message);
      setTurns((t) => { const n = t.slice(); const last = n[n.length - 1]; n[n.length - 1] = { ...last, text: last.text || msg }; return n; });
    } finally {
      setTurns((t) => { const n = t.slice(); const last = n[n.length - 1]; if (last?.role === 'assistant' && !last.text) n[n.length - 1] = { ...last, text: changed ? 'Done.' : 'No change.' }; return n; });
      inFlightRef.current = false;
      setBusy(false); setStatus(''); abortRef.current = null;
      const b = built as { kind?: string; pages: number } | null; // set inside the stream callback
      if (fresh && changed && b && b.pages > 1 && !ctrl.signal.aborted && aliveRef.current) setReady(b);
      onRunEnd(changed);
      if (!opts.voice) setTimeout(() => inputRef.current?.focus(), 0);
    }
    return said || (changed ? 'Done.' : 'No change.');
  };

  // Lykke the guide passed an instruction for this deck: it goes out like a typed message.
  const externalRef = useRef(0);
  useEffect(() => {
    if (externalAsk && externalAsk.n !== externalRef.current) { externalRef.current = externalAsk.n; void send(externalAsk.text); }
  }, [externalAsk]);

  // A deck started from Deck studio's box: its first message goes out as soon as the chat is up.
  useEffect(() => {
    if (initialAsk && !askedRef.current) { askedRef.current = true; onInitialAskUsed?.(); void send(initialAsk); }
  }, [initialAsk]);

  const toggleMic = () => {
    if (!SpeechRecognitionCtor) return;
    if (listening) { recRef.current?.stop(); return; }
    const rec = new SpeechRecognitionCtor();
    rec.lang = navigator.language || 'en-US'; rec.interimResults = true; rec.continuous = true;
    const base = draft ? `${draft.trim()} ` : '';
    rec.onresult = (e: any) => { let said = ''; for (let i = 0; i < e.results.length; i++) said += e.results[i][0].transcript; setDraft(base + said); };
    rec.onend = () => { setListening(false); recRef.current = null; };
    rec.onerror = () => { setListening(false); recRef.current = null; };
    recRef.current = rec; setListening(true); rec.start();
  };

  const empty = pageCount <= 1;
  const [brandIdeas, setBrandIdeas] = useState<string[] | null>(null);
  useEffect(() => { if (!empty) return; let off = false; coworkApi.presentations.ideas(brandId).then((r) => { if (!off && r.ideas?.length) setBrandIdeas(r.ideas); }).catch(() => undefined); return () => { off = true; }; }, [empty, brandId]);
  const ideas = empty ? (brandIdeas || START_IDEAS) : EDIT_IDEAS;

  // ── Read aloud (the browser's own voice: nothing leaves the device) ──
  const [speaking, setSpeaking] = useState<number | null>(null);
  const readAloud = (i: number, text: string) => {
    const synth = typeof window !== 'undefined' ? window.speechSynthesis : undefined; if (!synth) return;
    synth.cancel(); if (speaking === i) { setSpeaking(null); return; }
    const u = new SpeechSynthesisUtterance(text.replace(/\*\*/g, '').replace(/^\s*[-•*]\s+/gm, ''));
    u.rate = 1.08; u.onend = () => setSpeaking(null); u.onerror = () => setSpeaking(null);
    setSpeaking(i); synth.speak(u);
  };
  useEffect(() => () => window.speechSynthesis?.cancel(), []);
  const [copied, setCopied] = useState<number | null>(null);
  const copy = (i: number, text: string) => { void navigator.clipboard?.writeText(text).then(() => { setCopied(i); setTimeout(() => setCopied(null), 1400); }); };
  const lastAsk = (i: number) => { for (let j = i - 1; j >= 0; j--) if (turns[j].role === 'user') return turns[j].text; return ''; };

  // ── Call: talk with Lykke. Her voice (OpenAI Realtime) hears you and hands every request to the same deck agent
  // as the chat, so a call is this conversation spoken: the asks and her replies land here as turns. ──
  const callRef = useRef<RealtimeCall | null>(null);
  const [call, setCall] = useState<{ phase: 'connecting' | 'listening' | 'speaking' | 'working' | 'error'; caption?: string; heard?: string; error?: string; muted?: boolean } | null>(null);
  const patchCall = (p: Partial<NonNullable<typeof call>>) => setCall((c) => (c ? { ...c, ...p } : c));
  const indexRefCall = useRef(index); indexRefCall.current = index;
  const pagesRefCall = useRef(pageCount); pagesRefCall.current = pageCount;
  const endCall = () => { callRef.current?.end(); callRef.current = null; setCall(null); };
  useEffect(() => () => { callRef.current?.end(); callRef.current = null; }, []);
  const startCall = async () => {
    if (callRef.current) return;
    window.speechSynthesis?.cancel(); recRef.current?.stop();
    setCall({ phase: 'connecting' });
    try {
      const secret = await coworkApi.presentations.presentSession(presentationId, { mode: 'build' });
      const tools: RealtimeTool[] = [
        { name: 'ask_lykke', description: 'Do anything on the deck or answer anything about it: build a deck, change one page or all of them, add or move pages, change the look, check a figure, answer a question. request: what the person asked, in their own words, complete — every detail they gave. It returns her written reply once the deck has changed.',
          parameters: { type: 'object', properties: { request: { type: 'string' } }, required: ['request'] },
          execute: async (a) => {
            for (let k = 0; inFlightRef.current && k < 360; k++) await new Promise((r) => setTimeout(r, 500));
            patchCall({ phase: 'working' });
            const reply = await sendRef.current!(String(a.request || ''), { voice: true });
            patchCall({ phase: 'listening' });
            return { ok: true, reply: reply.slice(0, 1800), page_on_screen: indexRefCall.current + 1, pages: pagesRefCall.current };
          } },
        { name: 'go_to_page', silent: true, description: 'Show a page (1-based). Say nothing after it.', parameters: { type: 'object', properties: { page: { type: 'integer' } }, required: ['page'] },
          execute: async (a) => { const n = Math.max(1, pagesRefCall.current); const i = Math.max(0, Math.min(n - 1, (Number(a.page) || 1) - 1)); onEvent({ event: 'go_to', index: i }); return { ok: true, page_on_screen: i + 1, pages: n }; } },
      ];
      const instructions = `You are Lykke, the deck designer at Beatly, on a voice call with ${secret.userName || 'a colleague'} while they look at their deck in the editor${brandName ? ` (brand: ${brandName})` : ''}. The deck has ${pageCount} page(s); page ${index + 1} is on screen.
Talk like a person on a call: short turns (one or two sentences), warm, direct, the person's language.
For ANY change or question about the deck, call ask_lykke with their request in their own words, complete — never paraphrase details away. Say one short line while it works ("On it"). When it returns, say in one or two sentences what changed or the answer, from its reply — never read lists, sources or long numbers aloud; the details are in the chat.
go_to_page shows a page. Never invent figures. If something is unclear, ask one short question.`;
      const rc = new RealtimeCall(tools, {
        onStatus: (st) => {
          if (st === 'ended') { callRef.current = null; setCall(null); }
          else if (st === 'error') patchCall({ phase: 'error' });
          else if (st === 'listening') setCall((c) => (c && c.phase !== 'working' ? { ...c, phase: 'listening' } : c));
        },
        onTranscript: (role, text, final) => { if (role === 'user') patchCall({ heard: text }); else patchCall({ caption: final ? '' : text }); },
        onAudio: (playing) => setCall((c) => (c && c.phase !== 'working' ? { ...c, phase: playing ? 'speaking' : 'listening' } : c)),
        onError: (m) => patchCall({ phase: 'error', error: m }),
      });
      callRef.current = rc;
      await rc.connect(secret.clientSecret, secret.model, { instructions, voice: secret.voice, opening: `Say one short line: you're here, page ${index + 1} is on screen, and they can just tell you what they want.` });
      patchCall({ phase: 'listening' });
    } catch (e) {
      callRef.current?.end(); callRef.current = null;
      const m = (e as Error).message || '';
      setCall({ phase: 'error', error: /permission|NotAllowed/i.test(m) ? 'Microphone access is needed for a call — allow it in the browser and try again.' : 'The call could not start. Try again in a moment.' });
    }
  };

  sendRef.current = send;
  const [ideasOpen, setIdeasOpen] = useState(false);
  const icon = 'inline-flex h-7 w-7 items-center justify-center rounded-md text-[#78716c] hover:bg-[#f5f5f4] hover:text-[#1c1917]';
  return (
    <div className="flex h-full min-h-0 flex-col bg-[#fcfcfb]">
      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto px-5 py-5">
        <div className="flex flex-col gap-5">
          {!turns.length && (
            <div className="pt-6">
              <div className="flex items-center gap-2 text-[#be43ff]"><Sparkle className="h-5 w-5" /></div>
              <div className="mt-3 text-[20px] font-medium leading-snug text-[#1c1917]">{empty ? 'What deck do you need?' : 'What should change?'}</div>
              <div className="mt-1.5 text-[13.5px] leading-relaxed text-[#78716c]">
                {empty
                  ? `Say it like you would to a colleague — or call me. I use ${brandName ? `${brandName}'s` : 'the brand\'s'} campaigns, results and Brand OS and build the whole deck.`
                  : 'Anything: one page or all of them, new pages, order, look, a question. Click a page to edit it by hand.'}
              </div>
              <div className="mt-4 flex flex-col gap-2">
                {ideas.map((idea) => (
                  <button key={idea} onClick={() => (empty ? setDraft(idea) : void send(idea))} className="rounded-xl border border-[#e7e5e4] bg-white px-3.5 py-2.5 text-left text-[13.5px] text-[#44403c] hover:border-[#d6d3d1] hover:bg-[#fafaf9]">{idea}</button>
                ))}
              </div>
            </div>
          )}
          {turns.map((t, i) => (
            t.role === 'user'
              ? (
                <div key={i} className="flex justify-end">
                  <div className="max-w-[85%] whitespace-pre-wrap rounded-2xl bg-[#f0efec] px-3.5 py-2 text-[14px] leading-relaxed text-[#1c1917]">
                    {t.voice && <Phone className="mr-1.5 inline h-3 w-3 -translate-y-px text-[#a8a29e]" aria-label="Said on a call" />}{t.text}
                  </div>
                </div>
              )
              : (
                <div key={i} className="group">
                  <div className="text-[14.5px] leading-[1.65] text-[#292524]">{t.text ? <Said text={t.text} /> : (busy && i === turns.length - 1 ? null : '…')}</div>
                  {t.text && !(busy && i === turns.length - 1) && (
                    <div className={`mt-1.5 flex items-center gap-0.5 ${i === turns.length - 1 ? '' : 'opacity-0 transition-opacity group-hover:opacity-100'}`}>
                      <button className={icon} onClick={() => copy(i, t.text)} title="Copy" aria-label="Copy">{copied === i ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}</button>
                      <button className={icon} onClick={() => readAloud(i, t.text)} title={speaking === i ? 'Stop reading' : 'Read aloud'} aria-label="Read aloud">{speaking === i ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}</button>
                      {!busy && lastAsk(i) && <button className={icon} onClick={() => void send(lastAsk(i))} title="Ask again" aria-label="Ask again"><RotateCcw className="h-3.5 w-3.5" /></button>}
                    </div>
                  )}
                </div>
              )
          ))}
          {!busy && ready && (
            <div className={`rounded-xl border px-3.5 py-2.5 ${readiness?.clean ? 'border-[#abefc6] bg-[#f6fef9]' : 'border-[#fedf89] bg-[#fffcf5]'}`}>
              <div className="flex items-center gap-2">
                <span className={`flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-bold text-white ${readiness?.clean ? 'bg-[#17b26a]' : 'bg-[#f79009]'}`}>{readiness?.clean ? '✓' : readiness?.running ? '…' : '!'}</span>
                <span className={`text-[13px] font-semibold ${readiness?.clean ? 'text-[#085d3a]' : 'text-[#93370d]'}`}>
                  {KIND_WORD[ready.kind || ''] || 'Deck'} · {ready.pages} pages · {readiness?.running ? 'verifying the figures' : readiness?.open ? `${readiness.open} figure${readiness.open === 1 ? '' : 's'} not verified` : readiness?.clean ? `verified${readiness.verified ? ` · ${readiness.verified} figures` : ''}` : 'built'}
                </span>
              </div>
              <div className="mt-2 flex gap-1.5">
                {readiness && !readiness.running && readiness.open > 0 && onReview
                  ? <button onClick={onReview} className="rounded-full bg-[#1c1917] px-3 py-1 text-[12px] font-medium text-white hover:bg-[#292524]">Review {readiness.open} {readiness.open === 1 ? 'check' : 'checks'}</button>
                  : onShare && <button onClick={onShare} disabled={!!readiness?.running} className="rounded-full bg-[#1c1917] px-3 py-1 text-[12px] font-medium text-white hover:bg-[#292524] disabled:opacity-50">Share with client</button>}
                <button onClick={() => onEvent({ event: 'present' })} className="rounded-full border border-[#d6d3d1] bg-white px-3 py-1 text-[12px] font-medium text-[#44403c] hover:bg-[#fafaf9]">Present</button>
              </div>
            </div>
          )}
          {!busy && decisions.length > 0 && (
            <div className="flex flex-col gap-2">
              {decisions.map((d) => (
                <div key={d.question} className="rounded-xl border border-[#e7e5e4] bg-white px-3.5 py-2.5">
                  <div className="text-[13.5px] text-[#292524]">{d.question}</div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {/* The first option is what the deck already does: tapping it only closes the question. */}
                    {d.options.map((o, k) => k === 0
                      ? <button key={o} onClick={() => setDecisions((all) => all.filter((x) => x !== d))} className="rounded-full bg-[#1c1917] px-2.5 py-1 text-[12px] font-medium text-white hover:bg-[#292524]" title="The deck already uses this">✓ {o}</button>
                      : <button key={o} onClick={() => void send(o)} className="rounded-full border border-[#d6d3d1] bg-white px-2.5 py-1 text-[12px] font-medium text-[#44403c] hover:bg-[#fafaf9]">{o}</button>)}
                  </div>
                </div>
              ))}
            </div>
          )}
          {busy && (
            <div className="flex items-center gap-2 text-[13px] text-[#78716c]" aria-live="polite">
              <Sparkle className="h-4 w-4 animate-spin text-[#be43ff] [animation-duration:2.4s]" /> {status || 'Thinking'}
            </div>
          )}
        </div>
      </div>

      <div className="px-3 pb-3">
        {call ? (
          // The call, in place of the box: who is talking, what she is saying, mute and hang up.
          <div className="rounded-2xl border border-[#e7e5e4] bg-white p-3 shadow-[0_2px_10px_rgba(28,25,23,0.06)]">
            <div className="flex items-center gap-3">
              <span className="relative flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#f4ebff]">
                {(call.phase === 'speaking' || call.phase === 'listening') && <span className={`absolute inset-0 rounded-full bg-[#be43ff]/25 ${call.phase === 'speaking' ? 'animate-ping' : ''}`} />}
                <AudioLines className="relative h-4 w-4 text-[#7a1fbf]" />
              </span>
              <div className="min-w-0 flex-1">
                <div className="text-[13px] font-medium text-[#1c1917]">{call.phase === 'connecting' ? 'Calling Lykke…' : call.phase === 'speaking' ? 'Lykke is speaking' : call.phase === 'working' ? 'Lykke is working on the deck' : call.phase === 'error' ? 'Call problem' : call.muted ? 'Muted' : 'Listening'}</div>
                <div className="truncate text-[12px] text-[#78716c]">{call.error || call.caption || (call.heard ? `“${call.heard}”` : call.phase === 'working' ? status : 'Say what you want — she changes the deck as you talk.')}</div>
              </div>
              {call.phase !== 'error' && call.phase !== 'connecting' && (
                <button onClick={() => { const m = !call.muted; callRef.current?.setMuted(m); patchCall({ muted: m }); }} className={`inline-flex h-9 w-9 items-center justify-center rounded-full ${call.muted ? 'bg-[#1c1917] text-white' : 'bg-[#f5f5f4] text-[#44403c] hover:bg-[#e7e5e4]'}`} title={call.muted ? 'Unmute' : 'Mute'} aria-label={call.muted ? 'Unmute' : 'Mute'}>{call.muted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}</button>
              )}
              <button onClick={endCall} className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#d92d20] text-white hover:bg-[#b42318]" title="End call" aria-label="End call"><PhoneOff className="h-4 w-4" /></button>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-[#e7e5e4] bg-white shadow-[0_2px_10px_rgba(28,25,23,0.06)] focus-within:border-[#d6d3d1]">
            {ideasOpen && !busy && (
              <div className="flex flex-wrap gap-1.5 border-b border-[#f5f5f4] px-3 pb-2 pt-3">
                {(empty ? ideas : [...FOLLOW_UPS, ...EDIT_IDEAS]).map((f) => <button key={f} onClick={() => { setIdeasOpen(false); if (empty) setDraft(f); else void send(f); }} className="rounded-full border border-[#e7e5e4] px-2.5 py-1 text-[12px] text-[#44403c] hover:bg-[#fafaf9]">{f}</button>)}
              </div>
            )}
            <textarea
              ref={inputRef}
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(draft); } }}
              rows={Math.min(8, Math.max(2, draft.split('\n').length + Math.floor(draft.length / 48)))}
              placeholder={busy ? 'Lykke is working…' : listening ? 'Listening…' : empty ? 'Describe the deck you need' : 'Reply to Lykke…'}
              disabled={busy}
              className="block w-full resize-none bg-transparent px-4 pt-3 text-[14.5px] leading-relaxed text-[#1c1917] placeholder:text-[#a8a29e] focus:outline-none"
              aria-label="Message Lykke"
            />
            <div className="flex items-center gap-1 px-2 pb-2">
              <button onClick={() => setIdeasOpen((o) => !o)} disabled={busy} className={`${icon} h-8 w-8 rounded-lg ${ideasOpen ? 'bg-[#f5f5f4] text-[#1c1917]' : ''}`} title="Suggestions" aria-label="Suggestions"><Plus className="h-4 w-4" /></button>
              <span className="ml-auto" />
              {SpeechRecognitionCtor && !busy && (
                <button onClick={toggleMic} className={`${icon} h-8 w-8 rounded-lg ${listening ? '!bg-[#fee4e2] !text-[#d92d20]' : ''}`} title={listening ? 'Stop dictation' : 'Dictate'} aria-label={listening ? 'Stop dictation' : 'Dictate'}><Mic className="h-4 w-4" /></button>
              )}
              {busy
                ? <button onClick={() => { void coworkApi.presentations.chatStop(presentationId).catch(() => undefined); setStatus('Stopping…'); }} className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[#1c1917] text-white" title="Stop" aria-label="Stop"><Square className="h-3.5 w-3.5" /></button>
                : draft.trim()
                  ? <button onClick={() => void send(draft)} className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-[#be43ff] text-white hover:bg-[#a52fe6]" title="Send (Enter)" aria-label="Send"><ArrowUp className="h-4 w-4" /></button>
                  : <button onClick={() => void startCall()} className="inline-flex h-8 items-center gap-1.5 rounded-lg bg-[#1c1917] px-2.5 text-[12.5px] font-medium text-white hover:bg-[#292524]" title="Talk with Lykke" aria-label="Call Lykke"><AudioLines className="h-4 w-4" />Call</button>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
